import React from "react";
import { Link } from "react-router-dom";
import CardPlaceholder from "./CardPlaceholder";
import { formatCardNumber } from "../lib/cardNumber";
import { cardPath } from "../lib/slug";

/**
 * Abbreviate a rarity string to the first letter of each word.
 * Generic (no hardcoded lookup) so unseen rarities work.
 *   "Rare"                        -> "R"
 *   "Uncommon"                    -> "U"
 *   "Rare Holo"                   -> "RH"
 *   "Illustration Rare"           -> "IR"
 *   "Special Illustration Rare"   -> "SIR"
 *   "ACE SPEC Rare"               -> "ASR"
 *   "Rare Holo LV.X"              -> "RHLX" (dot stripped, letters keep)
 * Returns null for empty / non-string. Only used on set-page tiles;
 * the card-detail header keeps the full string per owner spec.
 */
export function abbreviateRarity(full) {
  if (typeof full !== "string" || !full.trim()) return null;
  const cleaned = full.replace(/[^\p{L}\p{N}\s]/gu, " ");
  const initials = cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
  return initials || null;
}

/**
 * Set-page thumbnail — Phase 7 renders ONE tile per card VARIANT (a
 * card with 3 printings surfaces as 3 tiles). The reference image is
 * shared per parent card (owner ruling R3); only the variant label + URL
 * differ per tile.
 *
 * Layout (2026-02-01, owner correction):
 *   ┌────────────────────────────────────┐
 *   │ [image]                            │
 *   ├────────────────────────────────────┤
 *   │ 12/102              RARE           │   ← number  |  RARITY (always)
 *   │ Ninetales                          │   ← card name
 *   │ $85.00               HOLO          │   ← price (left) | printing tag (right)
 *   └────────────────────────────────────┘
 *
 * Key rules (owner spec):
 * - Rarity is ALWAYS shown in its slot when the card has one — never
 *   replaced by the printing tag. Number+rarity row is untouched.
 * - Printing tag rides the price row (right-aligned) so a Holo Rare
 *   surfaces BOTH "RARE" (rarity, top row) and "HOLO" (printing tag,
 *   bottom row inline with price).
 * - `normal` variants render nothing on the right of the price row.
 * - Cards with no price still render the tag right-aligned on the
 *   same row (with an em-dash placeholder for the price).
 *
 * Props:
 *   variant   — a `card_variants` row shape (from GET /sets/{ext}/cards).
 *               Must include a nested `card` with the parent-card
 *               identity + reference image fields.
 *   set       — the parent set summary (optional; used for the
 *               "12/102" number format's official denominator).
 */
export default function CardTile({ variant, set }) {
  const card = variant.card || {};
  const priceLabel =
    variant.effective_price != null
      ? `$${Number(variant.effective_price).toFixed(2)}`
      : null;
  const testKey = `${card.external_id || variant.card_external_id}-${variant.slug}`;

  const numberDisplay = formatCardNumber({
    localId: card.number || variant.number,
    cardCountOfficial: set ? set.card_count_official : null,
    setCode: set ? set.code || set.external_id : null,
  });

  const printingLabel = variant.variant_label; // null for normal, truthy for holo/etc.
  const rarityLabel = card.rarity;

  return (
    <Link
      to={cardPath({
        game: "pokemon",
        lang: "en",
        setName: set?.name || variant.set_external_id || card.set_external_id,
        cardName: card.name,
        number: card.number || variant.number,
        variantSlug: variant.slug,
        defaultVariantSlug: card.default_variant_slug,
      })}
      data-testid={`card-tile-${testKey}`}
      className="animate-rise group flex flex-col gap-2 rounded-card border border-ink-chip bg-ink-surface p-2 transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
    >
      <div className="rounded-card overflow-hidden">
        {card.reference_thumbnail_url || card.reference_image_url ? (
          <img
            src={card.reference_thumbnail_url || card.reference_image_url}
            alt={set?.name ? `${card.name} from ${set.name}` : card.name}
            className="w-full object-cover"
            style={{ aspectRatio: "5 / 7" }}
            loading="lazy"
          />
        ) : (
          <CardPlaceholder label={card.name} />
        )}
      </div>

      {/* Row 1: number (left) | RARITY chip (right, always shown when present) */}
      <div className="flex items-baseline justify-between gap-2 px-1">
        <span
          data-testid={`card-tile-number-${testKey}`}
          className="text-meta text-ink-muted tabular"
        >
          {numberDisplay}
        </span>
        {rarityLabel && (
          <span
            data-testid={`card-tile-rarity-${testKey}`}
            title={rarityLabel}
            aria-label={rarityLabel}
            className="text-eyebrow uppercase text-ink-muted tabular"
          >
            {abbreviateRarity(rarityLabel)}
          </span>
        )}
      </div>

      {/* Card name */}
      <div className="px-1 text-card-name text-ink-text line-clamp-2">
        {card.name}
      </div>

      {/* Row 3: price (left) | printing tag (right, only for non-normal variants) */}
      <div className="mt-auto flex items-baseline justify-between gap-2 px-1 pb-1">
        <span className="text-meta tabular">
          {priceLabel ? (
            <span className="text-ink-text">{priceLabel}</span>
          ) : (
            <span className="text-ink-muted">—</span>
          )}
        </span>
        {printingLabel ? (
          <span
            data-testid={`card-tile-printing-${testKey}`}
            className="rounded-chip border border-indigo-chip-border bg-indigo-chip-bg px-2 py-[1px] text-eyebrow uppercase text-indigo-chip-text"
          >
            {printingLabel}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
